//components/onboarding/consultation/ConsultStep4.tsx

"use client";

import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useState, useRef } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useCountries, useStates, useCities } from "@/lib/useLocation";

type Step4Props = {
  onBack: () => void;
  onNext: (data: { country: string; state: string; city: string }) => void;
  defaultCountry?: string;
  defaultState?: string;
  defaultCity?: string;
  progressBar?: React.ReactNode;
};

export default function ConsultStep4({
  onBack,
  onNext,
  defaultCountry = "Nigeria",
  defaultState = "",
  defaultCity = "",
  progressBar,
}: Step4Props) {
  const shouldReduceMotion = useReducedMotion();
  const motionProps = shouldReduceMotion
    ? {}
    : { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };

  // Track whether defaults have already been auto-selected
  const hasAutoSelectedCountry = useRef(false);
  const hasAutoSelectedState = useRef(false);
  const hasAutoSelectedCity = useRef(false);

  // State
  const [countryIso, setCountryIso] = useState("");
  const [stateIso, setStateIso] = useState("");
  const [city, setCity] = useState("");

  // SWR
  const { data: countries = [], isLoading: loadingCountries } = useCountries();
  const { data: states = [], isLoading: loadingStates } = useStates(countryIso);
  const { data: cities = [], isLoading: loadingCities } = useCities(
    countryIso,
    stateIso
  );

  // Auto-select default country
  useEffect(() => {
    if (!countries.length || hasAutoSelectedCountry.current) return;

    const match = countries.find((c) => c.name === defaultCountry);
    if (match) {
      setCountryIso(match.iso2);
      hasAutoSelectedCountry.current = true;
    }
  }, [countries, defaultCountry]);

  // Auto-select default state
  useEffect(() => {
    if (!states.length || hasAutoSelectedState.current) return;

    const match = states.find((s) => s.name === defaultState);
    if (match) {
      setStateIso(match.iso2);
      hasAutoSelectedState.current = true;
    }
  }, [states, defaultState]);

  // Auto-select default city
  useEffect(() => {
    if (!cities.length || hasAutoSelectedCity.current) return;

    const match = cities.find((c) => c.name === defaultCity);
    if (match) {
      setCity(match.name);
      hasAutoSelectedCity.current = true;
    }
  }, [cities, defaultCity]);

  // Handlers
  const handleCountryChange = (iso: string) => {
    setCountryIso(iso);
    setStateIso("");
    setCity("");

    // user changed it manually; stop auto-select overrides
    hasAutoSelectedState.current = true;
    hasAutoSelectedCity.current = true;
  };

  const handleStateChange = (iso: string) => {
    setStateIso(iso);
    setCity("");

    hasAutoSelectedCity.current = true;
  };

  const handleContinue = () => {
    const countryName =
      countries.find((c) => c.iso2 === countryIso)?.name || "";
    const stateName = states.find((s) => s.iso2 === stateIso)?.name || "";
    const cityName = cities.find((c) => c.name === city)?.name || "";

    if (countryName && stateName && cityName) {
      onNext({ country: countryName, state: stateName, city: cityName });
    }
  };

  const isContinueDisabled = !countryIso || !stateIso || !city || loadingCities;

  return (
    <div className="max-w-md mx-auto">
      <motion.h1
        {...motionProps}
        className="text-2xl md:text-3xl font-semibold text-gray-800"
      >
        What's your preferred location?
      </motion.h1>

      <motion.p
        {...motionProps}
        transition={shouldReduceMotion ? {} : { delay: 0.1 }}
        className="mt-2 text-sm text-gray-600"
      >
        Share your location so we can connect you with the best solar providers
        near you.
      </motion.p>

      {progressBar}

      <div className="mt-8 space-y-6">
        {/* Country */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Country
          </label>
          <Select
            value={countryIso}
            onValueChange={handleCountryChange}
            disabled={loadingCountries}
          >
            <SelectTrigger className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-600">
              <SelectValue
                placeholder={
                  loadingCountries
                    ? "Loading countries..."
                    : "Select your country"
                }
              />
            </SelectTrigger>

            <SelectContent>
              {countries.map((c) => (
                <SelectItem key={c.iso2} value={c.iso2}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* State */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            State / Region
          </label>

          <Select
            value={stateIso}
            onValueChange={handleStateChange}
            disabled={!countryIso || loadingStates}
          >
            <SelectTrigger className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-600">
              <SelectValue
                placeholder={
                  loadingStates
                    ? "Loading states..."
                    : countryIso
                      ? "Select your state"
                      : "First select a country"
                }
              />
            </SelectTrigger>

            <SelectContent>
              {states.map((s) => (
                <SelectItem key={s.iso2} value={s.iso2}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* City */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            City
          </label>

          <Select
            value={city}
            onValueChange={setCity}
            disabled={!stateIso || loadingCities}
          >
            <SelectTrigger className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-600">
              <SelectValue
                placeholder={
                  loadingCities
                    ? "Loading cities..."
                    : stateIso
                      ? "Select your city"
                      : "First select a state"
                }
              />
            </SelectTrigger>

            <SelectContent>
              {cities.map((c) => (
                <SelectItem key={c.name} value={c.name}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Buttons */}
      <div className="mt-10 flex justify-end items-center gap-6">
        <Button
          onClick={onBack}
          size="lg"
          variant="outline"
          className="rounded-full border-gray-300 text-gray-700 hover:bg-gray-100"
        >
          Back
        </Button>

        <motion.button
          whileTap={{ scale: shouldReduceMotion ? 1 : 0.95 }}
          onClick={handleContinue}
          disabled={isContinueDisabled}
          className={`
            px-8 py-3 rounded-full font-medium text-white transition-all
            ${isContinueDisabled
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-green-800 hover:bg-green-700 shadow-lg hover:shadow-xl"
            }
          `}
        >
          Continue
        </motion.button>
      </div>
    </div>
  );
}
